import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isPresent, isEmpty } from '@ember/utils'
import { task, timeout } from 'ember-concurrency'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import { starImages } from 'teacher-ui-client/components/tables/fluency-sprints/star-assets'
import { format } from 'date-fns'
import type { IntlService } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import { tracked } from '@glimmer/tracking'
import type { FilterDataItem } from '@blakeelearning/data-tables/utils/filtering'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import { waitFor } from '@ember/test-waiters'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import ImageCell from 'teacher-ui-client/components/stable-table/basic-cells/image'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import { hash } from '@ember/helper'
import { t } from 'ember-intl'
import { and, not } from 'ember-truth-helpers'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type {
  MentalMinuteStudentAggregateData,
  MentalMinuteStudentLessonItem,
} from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-student'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type { FormatValueOptions } from 'teacher-ui-client/utils/format-value'

export interface FluencySprintsStudentOverviewData {
  aggregate: MentalMinuteStudentAggregateData
  lessons: MentalMinuteStudentLessonItem[]
}

interface Args {
  data: Nullable<FluencySprintsStudentOverviewData>
  sortingConfig?: SortingConfig
  category?: string
  onSortClick?: ({ sortKey }: SortObject) => void
  exportable?: boolean
}

interface Signature {
  Args: Args
}

interface TransformedData {
  items: (FilterDataItem & MentalMinuteStudentLessonItem)[]
  paginated?: FilterDataItem[]
  pageCount?: number
}

interface Column {
  value?: string
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
  cellComponent?: typeof ImageCell
  formatEmptyConfig?: FormatValueOptions
}

export class FluencySprintsStudentOverview extends Component<Signature> {
  @service intl!: IntlService
  @service dataTransformer!: DataTransformerService
  @service lessonInfo!: LessonInfoService

  @tracked transformedData: TransformedData = {
    items: [],
    paginated: [],
    pageCount: 0,
  }

  get pipelineDescriptors() {
    return [this.filterDescriptor, this.sortingDescriptor]
  }

  get sortingDescriptor() {
    const tableColumns = this.columns
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort', config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  get filterDescriptor() {
    const { category: filteringCategory } = this.args

    if (isEmpty(filteringCategory)) return []

    const filterConfig = { itemFilter: (item: MentalMinuteStudentLessonItem) => item.category === filteringCategory }

    return { type: 'filter', config: filterConfig }
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  performTransformItems = (
    _element: unknown,
    [items, pipelineDescriptors]: [MentalMinuteStudentLessonItem[] | undefined, PipelineDescriptor[]],
  ) => {
    void this.transformItems.perform(items, pipelineDescriptors)
  }

  transformItems = task(
    { restartable: true },
    waitFor(async (items: MentalMinuteStudentLessonItem[] = [], pipelineDescriptors: PipelineDescriptor[] = []) => {
      await timeout(200) // Debounce

      const presentDescriptors = pipelineDescriptors.filter(isPresent)
      this.transformedData = await this.dataTransformer.buildAndTransform(items, presentDescriptors)
    }),
  )

  get transformedItems() {
    return this.transformedData.items
  }

  get columns(): Column[] {
    const { intl } = this
    const translationPrefix = 'tables.fluencySprints.studentOverview.headers'

    const defaultColumn: Column = {
      classNames: 'table-box table-box-header bg-ms-green-100',
      cellClassNames: 'table-box table-box-cell bg-ms-green-50 group-hover:bg-ms-green-100',
      component: SortableCell,
      comparerKey: 'sortByNumber',
    }

    const columns: Column[] = [
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.area`),
        cellClassNames: `${defaultColumn.cellClassNames} print:wrap-break-word`,
        valuePath: 'translatedCategory',
        sortKey: 'category',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.sprintsMastered`),
        valuePath: 'lessonLabel',
        sortKey: 'lesson',
        comparerKey: 'sortByNumber',
      },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.numberAttempts`), valuePath: 'attempts' },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.recentAttempt`),
        valuePath: 'mostRecentAttempt',
        sortKey: 'last_attempted_at',
        comparerKey: 'sortByNumber',
      },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.firstScore`), valuePath: 'first_score' },
      { ...defaultColumn, value: intl.t(`${translationPrefix}.bestScore`), valuePath: 'best_score' },
      {
        ...defaultColumn,
        classNames: `${defaultColumn.classNames} print:wrap-break-word`,
        value: intl.t(`${translationPrefix}.improvement`),
        valuePath: 'improvement',
        formatEmptyConfig: { append: '%' },
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.level`),
        valuePath: 'starsImage',
        sortKey: 'stars',
        cellComponent: ImageCell,
      },
    ]

    return columns
  }

  get lessonRows() {
    const { intl } = this
    const numberOfColumns = this.columns.length

    return this.transformedItems.map((lesson: MentalMinuteStudentLessonItem) => {
      const translatedCategory = intl.t(`tables.fluencySprints.studentOverview.categories.${lesson.category}`)

      // Build the lesson label.
      const lessonId = lesson.lesson
      const lessonName = this.lessonInfo
        .lessonsFor(Precinct.MS_MENTAL_MINUTE)
        .find((lesson) => lesson.lesson_number === lessonId)?.name
      const lessonLabel = `${lessonId}: ${lessonName}`

      const dateFormat = intl.t('dateFormats.dateFns.dd MMM')
      const mostRecentAttempt = format(new Date(lesson.last_attempted_at * 1000), dateFormat)

      const starImageWidth = 11 + 10 * lesson.stars
      let starsImage = {}

      if (lesson.stars > 0) {
        starsImage = {
          src: starImages[lesson.stars],
          alt: `${lesson.stars} star(s)`,
          height: 21,
          width: starImageWidth,
          export: lesson.stars.toString(),
        }
      }

      const cells = Array(numberOfColumns).fill({})

      return {
        ...lesson,
        classNames: 'table-row-striped group',
        translatedCategory,
        lessonLabel,
        mostRecentAttempt,
        starsImage,
        cells,
      }
    })
  }

  get footerRows() {
    const { intl } = this
    const aggregate = this.args.data?.aggregate
    if (!aggregate) return []

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    const averageRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.studentOverview.footers.average') },
        emptyCell,
        { value: aggregate?.attempts.average },
        emptyCell,
        { value: aggregate?.scores.average_first_score },
        { value: aggregate?.scores.average_best_score },
        { value: aggregate?.scores.average_improvement, formatEmptyConfig: { append: '%' } },
        emptyCell,
      ],
    }

    const totalsRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.studentOverview.footers.totals') },
        emptyCell,
        { value: aggregate?.attempts.total },
        emptyCell,
        emptyCell,
        emptyCell,
        emptyCell,
        emptyCell,
      ],
    }

    const classNames = 'table-footer-cell border-t border-ms-green-100 print:border-black'
    averageRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })
    totalsRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })

    return [averageRow, totalsRow]
  }

  <template>
    <BasicTable
      {{applyTransform this.performTransformItems @data.lessons this.pipelineDescriptors}}
      class="w-full"
      exportable={{@exportable}}
      @columns={{this.columns}}
      @rows={{this.lessonRows}}
      @footerRows={{this.footerRows}}
      @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
      as |bt|
    >
      <bt.head as |h|>
        {{! Custom grouped header }}
        <tr data-test-grouped-header-row>
          <th class="p-3 print:pl-0" colspan="4">
            <div class="font-bold">{{t "tables.fluencySprints.studentOverview.headers.mentalMinute"}}</div>
            <div class="font-normal">{{t "tables.fluencySprints.studentOverview.headers.numberFactFluency"}}</div>
          </th>
          <th class="table-box table-box-header bg-ms-green-150 text-center" colspan="3">
            {{t "tables.fluencySprints.studentOverview.headers.numberFactsPerMinute"}}
          </th>
          <th class="table-box table-box-header bg-ms-green-150 text-center">
          </th>
        </tr>
        {{! Standard header }}
        <h.row />
      </bt.head>
      {{! If not transforming and no items to show, always only display the empty message }}
      {{#if (and this.transformItems.isIdle (not this.lessonRows))}}
        <bt.spanner class="p-3 text-center italic">
          {{t "tables.fluencySprints.emptyTableMessage"}}
        </bt.spanner>
      {{else}}
        {{! If transforming, always show a loading spinner}}
        {{! When no items, show it normally. }}
        {{! If there are items, place it on top of the existing table body (absolute) }}
        {{#if this.transformItems.isRunning}}
          <bt.spanner
            class="table-box table-box-cell border-transparent
              {{if this.lessonRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
          >
            <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
          </bt.spanner>
        {{/if}}
        <bt.body class={{if this.transformItems.isRunning "opacity-50"}} />
      {{/if}}
      {{#if @data.aggregate}}
        <bt.foot />
      {{/if}}
    </BasicTable>
  </template>
}

export default FluencySprintsStudentOverview
